import { Form } from "react-bootstrap"
import { FieldValues, useController } from "react-hook-form"
import { ManagedPropsWithOptionalLabel } from "./type"
import React, { useId, useCallback, useRef, useEffect, useMemo } from "react"
import ReactQuill, { type DeltaStatic, type EmitterSource } from "react-quill-new"

export type { DeltaStatic }
import "react-quill-new/dist/quill.snow.css"
import { PopopoImageBlotAttributes, registerPopopoImageBlot } from "../../quill/PopopoImageBlot"
import { restrictImagePasteMatchers } from "../../quill/clipboardMatchers"
import { ManagedImageId } from "popopo-msg"
import { z } from "zod/v4"

/**
 * QuillEditor の Delta コンテンツ用 Zod スキーマ。
 * バリデーションは常に通過（実際の構造は Quill が管理するため）。
 * z.record(z.string(), z.unknown()) の代わりに使用する。
 */
export const zQuillDelta = z.custom<DeltaStatic>(() => true)

/**
 * DeltaStatic を API 送信用の Record<string, unknown> に変換する。
 * API の型定義が Record<string, unknown> を期待しているため、
 * フォームデータを API リクエストに変換する際に使用する。
 */
export function toQuillDeltaRecord(delta: DeltaStatic): Record<string, unknown> {
  return { ops: delta.ops }
}

type UnprivilegedEditor = {
  getLength(): number
  getText(): string
  getHTML(): string
  getContents(): DeltaStatic
  getSelection(): { index: number; length: number } | null
  getBounds(index: number, length?: number): { left: number; top: number; height: number; width: number } | null
}

export type InsertImageParams = {
  imageUrl: string
  attributes?: {
    [PopopoImageBlotAttributes.managedImageId]?: ManagedImageId
  }
}

export type InsertImageFunction = (params: InsertImageParams) => void

type Props<TFieldValues extends FieldValues, TContext = unknown, TTransformedValues = TFieldValues> = ManagedPropsWithOptionalLabel<TFieldValues, TContext, TTransformedValues> & {
  placeholder?: string
  readOnly?: boolean
  minHeight?: number
  openImageSelector?: (insertImage: InsertImageFunction) => void
}

export function ManagedQuillEditor<TFieldValues extends FieldValues, TContext = unknown, TTransformedValues = TFieldValues>
({
   control,
   name,
   label,
   helper,
   required,
   placeholder,
   readOnly = false,
   minHeight = 150,
   openImageSelector,
   ...quillProps
 }: Props<TFieldValues, TContext, TTransformedValues>) {
  const id = useId()
  const { field, fieldState } = useController({ name, control })
  const quillRef = useRef<ReactQuill>(null)

  useEffect(() => {
    registerPopopoImageBlot()
  }, [])

  const handleImageInsert = useCallback(() => {
    if (!openImageSelector || !quillRef.current) return

    const insertImage: InsertImageFunction = (params) => {
      if (!quillRef.current) return

      const quill = quillRef.current.getEditor()
      const selection = quill.getSelection() || { index: quill.getLength(), length: 0 }

      quill.insertEmbed(selection.index, "image", params.imageUrl)

      if (params.attributes) {
        quill.formatText(selection.index, 1, params.attributes)
      }
    }

    openImageSelector(insertImage)
  }, [openImageSelector])

  const modules = useMemo(() => ({
    clipboard: {
      matchers: restrictImagePasteMatchers
    },
    uploader: {
      handler: () => {
        // 何もしない空のハンドラ
        // File/Blobの画像アップロードを無効化
        // 画像管理機能（insertEmbed経由）は影響を受けない
      }
    },
    toolbar: {
      container: [
        [{ "header": [1, 2, false] }],
        ["bold", "italic", "underline"],
        [{ "list": "ordered" }, { "list": "bullet" }],
        ["link"],
        ...(openImageSelector ? [["image"]] : []),
        ["clean"]
      ],
      handlers: openImageSelector ? {
        image: handleImageInsert
      } : {}
    }
  }), [openImageSelector, handleImageInsert])

  const formats = [
    "header",
    "bold", "italic", "underline",
    "list",
    "link",
    ...(openImageSelector ? ["image"] : [])
  ]

  const handleChange = useCallback((content: string, delta: DeltaStatic, source: EmitterSource, editor: UnprivilegedEditor) => {
    const deltaContents = editor.getContents()
    field.onChange(deltaContents)
  }, [field])

  const handleBlur = useCallback(() => {
    field.onBlur()
  }, [field])


  return (
    <>
      <Form.Group controlId={id}>
        {
          label == null ? null : (
            <Form.Label>
              {label}
              {required ? <span className="text-danger">*</span> : null}
            </Form.Label>
          )
        }
        <div className="quill-container">
          <ReactQuill
            {...quillProps}
            ref={quillRef}
            theme="snow"
            value={field.value}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            readOnly={readOnly}
            modules={modules}
            formats={formats}
          />
          <style>{`
            .quill-container .ql-editor {
              min-height: ${minHeight}px;
            }
            .quill-container .ql-editor img {
              max-height: 200px;
              width: auto;
            }
          `}</style>
        </div>
        {fieldState.error ? <Form.Text className="text-danger">{fieldState.error.message}</Form.Text> : helper != null ? <Form.Text className="text-muted">{helper}</Form.Text> : null}
      </Form.Group>
    </>
  )
}
